import moment from "moment";

import { LabMessageDTO, MyleNoteDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { LabInboxes, LabSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LabClinicianTableCell } from "@/Labs/Components/LabClinicianTableCell";
import { LabReportCombinedStatusComponent } from "@/Labs/Components/LabReportCombinedStatusComponent";
import { LabReportReadingStatusComponent } from "@/Labs/Components/LabReportReadingStatusComponent";
import { LabsInboxSettingsContainer } from "@/Labs/Components/LabsInboxSettingsContainer";
import { LabMessageTableItem } from "@/Labs/Models/LabMessageTableItem";
import { ILabsInboxModel } from "@/Labs/Models/UI/ILabsInboxModel";
import { SendResultStrategy } from "@/Labs/Services/Portal/Strategies/SendResultStrategy";
import { BcCareConnectButton } from "@/Patient/Components/BcCareConnectButton";
import { BcCareConnectDomain } from "@/Patient/Models/BcCareConnect/BcCareConnect";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { PatientLabsInboxViewButtonRegistryView } from "./Extensions/PatientLabsInboxViewButtonRegistry";

interface IPatientLabsInboxViewProps {
    Model: ILabsInboxModel;
    PatientContactId?: string;
    PatientModel?: Patient;
    IsDSQActive?: boolean;
    isReadOnly?: boolean;
}

class PatientLabsInboxView extends BaseReactComponent<IPatientLabsInboxViewProps> {
    private isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;
    private helper = cacheStore.Helper;

    public selectedTableItemIDs: string[];

    public render(): JSX.Element {
        const canFileAndHold = sessionStore.canApproveDocument() && !this.props.isReadOnly;

        // prepare The Grid
        const SaveOptionKey = "LabGridSettingsPatientProfile"; // Unique Key that will be used to Append/Load your Grid Settings in the DataBase in a JSON format to [dbo].[Login].[UserSettings]

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(SaveOptionKey, this.GetMyleTableColumnsDefaultOptions() /* Default Options */);
        const tableColumns = this.getTableColumns(columnsOptions); // Add Rendering Functions
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions); // Table Options
        const columnConfigModel = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        this.scanForSelectedItemsInTheTable();

        return (
            <div className="sb-workspace inherit-height">
                {this.props.Model.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.Model.get("PortalLinkedItemModel")}
                        strategy={new SendResultStrategy()}
                    />
                )}
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("LabsLabel")}
                    additionalLeftComponent={this.renderTitleButtons()}
                    searchText={this.props.Model.get("GlobalLabsearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchLabel")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Labs.SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Labs.OnRefreshClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Labs.LabInboxSearchSettings_ShowOptionsButtonClick)}
                />

                <div className="tb ws-scrollable">
                    {this.props.Model.get("LabInboxDisplaySettingsOption") && (
                        <div onClick={this.onContainerClick} className="filter-component" id="labs_search_options_container" style={{ right: "50px", width: "auto" }}>
                            <LabsInboxSettingsContainer LabViewModel={this.props.Model} />
                        </div>
                    )}

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="labinbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("LabsTableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Labs.Table_InboxRowClick}
                            SaveOptionskeyTarget={SaveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_LabInbox_Search_ColumnSortClick}
                        />
                    </div>

                    <div className="clearfix" />
                </div>

                <div className="ws-subfooter table-pager-footer">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-6">
                            <SimplePagerControl PageSize={this.props.Model.get("SelectedPageSizeOption")} TotalRows={this.props.Model.get("TotalQueryRows")} CurrentPage={this.props.Model.get("CurrentPage")} EventToDispatchOnPageChange={LocalOperations.Labs.CurrentPageChange} />
                        </div>
                        {canFileAndHold && (
                            <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                                <ButtonControl
                                    className="btn btn-primary push-right-5"
                                    label={cacheStore.GetString("OnHoldLabAction")}
                                    disabled={!(this.selectedTableItemIDs.length > 0)}
                                    onClick={() => sessionStore.dispatch(
                                        LocalOperations.Labs.Inbox_ButtonClick_HoldLabs,
                                        { item: this.selectedTableItemIDs }
                                    )}
                                />
                                <ButtonControl
                                    className="btn btn-primary"
                                    label={cacheStore.GetString("FileLabAction")}
                                    disabled={!(this.selectedTableItemIDs.length > 0)}
                                    onClick={() => sessionStore.dispatch(
                                        LocalOperations.Labs.Inbox_ButtonClick_FileLabs,
                                        { item: this.selectedTableItemIDs }
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigModel}
                        SaveOptionskeyTarget={SaveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Labs.LabInboxColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private renderTitleButtons(): JSX.Element[] {
        const buttons: JSX.Element[] = [];

        if (!this.props.isReadOnly && (patientStore.isDSQActive() || sessionStore.isBcCareConnectActive())) {
            buttons.push(
                <div
                    style={{ display: "inherit", alignItems: "center", flexWrap: "nowrap", gap: "10px" }}
                    key="dsqAndCareConnectButtons"
                >
                    {patientStore.isDSQActive() && this.renderDsqButtons()}
                    {sessionStore.isBcCareConnectActive() && this.renderBcCareConnectButton()}
                </div>
            );
        }

        buttons.push(
            <PatientLabsInboxViewButtonRegistryView patientContactId={patientStore.getCurrentPatientContactId()} providerContactFacetId={cacheStore.GetCurrentEmployee().ContactFacet_id} />
        );

        return buttons;
    }

    private renderDsqButtons(): JSX.Element[] {
        return [
            <ButtonControl
                key="dsqlabs"
                className="btn btn-primary"
                onClick={() => this.onDSQLabsClick()}
                label={cacheStore.GetString("btnDSQLabsInbox")}
            />,
            <ButtonControl
                key="dsqimgs"
                className="btn btn-primary"
                onClick={() => this.onDSQImgsClick()}
                label={cacheStore.GetString("btnDSQImgsInbox")}
            />
        ];
    }

    private renderBcCareConnectButton(): JSX.Element {
        return (
            <BcCareConnectButton
                key="careconnect"
                patient={this.props.PatientModel}
                domain={BcCareConnectDomain.Labs}
            />
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public scanForSelectedItemsInTheTable(): void {
        // Compute SelectedTableItemIDs
        this.selectedTableItemIDs = [];
        this.props.Model.get("LabsTableData").forEach(item => {
            if (item.IsSelected) { this.selectedTableItemIDs.push(item.Model.Model.LabFillerID); }
        });
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    public ResolveCurrentInboxName(LabInbox: LabInboxes): string {
        if (LabInbox == LabInboxes.All)
            return cacheStore.GetString("AllLabInbox"); // "All Lab Inbox";
        if (LabInbox == LabInboxes.New)
            return cacheStore.GetString("NewLabsInbox");
        if (LabInbox == LabInboxes.Suspended)
            return cacheStore.GetString("OnHoldLabsInbox");
        if (LabInbox == LabInboxes.Classified)
            return cacheStore.GetString("FiledLabsInbox");
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const isPortalColumnEnable = this.isPortalEnabled && !this.props.isReadOnly;
        const labClinicianHeaderText = cacheStore.GetString(
            cacheStore.ClinicSettings.EnableManualEntryOfLabResults ? "DoctorsLabel" : "PrescribedByLabel"
        );

        if (cacheStore.ClinicSettings.EnableResultsFixes) {
            return [
                new MyleTableColumnOptions({
                    HeaderText: "",
                    FieldName: "LabCheckBox",
                    WidthPixels: 38
                }),
                new MyleTableColumnOptions({
                    HeaderText: labClinicianHeaderText,
                    FieldName: "LabClinician",
                    RowClickEnabled: true,
                    WidthPercentageDefault: (isPortalColumnEnable) ? 8 : 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("SendingFacilityLabel"),
                    FieldName: "SendingFacility",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("ContextLabel"),
                    FieldName: "Context",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("SampleDateLabel"),
                    FieldName: "LabMessageDate",
                    RowClickEnabled: true,
                    IsSortable: true,
                    SortColumn: LabSortOrderType.MessageDate,
                    SortDirection: SortDirection.DESC,
                    WidthPercentageDefault: (isPortalColumnEnable) ? 17.1 : 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("DescriptionLabel"),
                    FieldName: "MessageSummary",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("StatusLabel"),
                    FieldName: "Status",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                isPortalColumnEnable
                    ? new MyleTableColumnOptions({
                        HeaderText: cacheStore.GetString("StatusPortal"),
                        FieldName: "PortalStatus",
                        RowClickEnabled: false,
                        IsSortable: true,
                        SortColumn: LabSortOrderType.PortalStatus,
                        SortDirection: SortDirection.ASC,
                        WidthPercentageDefault: 8
                    })
                    : null,
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("DetailsLabel"),
                    FieldName: "Detail",
                    RowClickEnabled: true,
                    WidthPercentageDefault: 12
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ActionLabel" : "NotesDLabel"),
                    FieldName: "Action",
                    WidthPercentageDefault: (isPortalColumnEnable) ? 5 : 8,
                    RowClickEnabled: false
                })
            ].filter(col => col != null);
        }

        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "LabCheckBox",
                WidthPixels: 38
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PrescribedByLabel"),
                FieldName: "LabClinician",
                RowClickEnabled: true,
                WidthPercentageDefault: (isPortalColumnEnable) ? 8 : 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SendingFacilityLabel"),
                FieldName: "SendingFacility",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ContextLabel"),
                FieldName: "Context",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SampleDateLabel"),
                FieldName: "LabMessageDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: LabSortOrderType.MessageDate,
                SortDirection: SortDirection.DESC,
                WidthPercentageDefault: (isPortalColumnEnable) ? 17.1 : 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionLabel"),
                FieldName: "MessageSummary",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "Status",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            isPortalColumnEnable
                ? new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("StatusPortal"),
                    FieldName: "PortalStatus",
                    RowClickEnabled: false,
                    IsSortable: true,
                    SortColumn: LabSortOrderType.PortalStatus,
                    SortDirection: SortDirection.ASC,
                    WidthPercentageDefault: 8
                })
                : null,
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ActionLabel" : "NotesDLabel"),
                FieldName: "Action",
                WidthPercentageDefault: (isPortalColumnEnable) ? 5 : 8,
                RowClickEnabled: false
            })
        ];
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const tableCellClass = "text-wrap-control";

        return MyleTableColumn.buildList<LabMessageTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "LabCheckBox":
                        return {
                            renderCell: item => (
                                <div>
                                    {item.isSelectable && (
                                        <CheckboxControl
                                            id={item.Model.Model.LabFillerID}
                                            checked={item.IsSelected}
                                            operation={LocalOperations.Labs.CheckLabMessageItem}
                                            item={item}
                                        />
                                    )}
                                </div>
                            ),
                            renderHeader: () => (
                                <div className="checkbox-col">
                                    <CheckboxControl
                                        checked={this.props.Model.get("SelectAllRowItemsChecked")}
                                        className="header-input"
                                        operation={LocalOperations.Labs.CheckAllLabMessageItem}
                                    />
                                </div>
                            )
                        };

                    case "LabClinician":
                        return {
                            renderCell: item => (
                                <FeatureFlagControl
                                    activeFeature={(
                                        <LabClinicianTableCell
                                            clinicians={item.Model.Model.IsManualLabEntry ? [item.Model.getPrescriptorDTO(), ...item.Model.getCarbonCopiesCDTO()] : [item.Model.getPrescriptorDTO()]}
                                            isRed={item.Model.IsAbnormal}
                                            isBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            cellClassName={tableCellClass}
                                        />
                                    )}
                                    fallbackFeature={(
                                        <div title={item.Model.ClinicianPrescriptorName}>
                                            <SpanControl
                                                IsRed={item.Model.IsAbnormal}
                                                IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                                className={tableCellClass}
                                                text={item.Model.ClinicianPrescriptorName}
                                            />
                                        </div>
                                    )}
                                    flag={cacheStore.ClinicSettings.EnableManualEntryOfLabResults && cacheStore.ClinicSettings.EnableCentralizedManualLabsGrid}
                                />
                            ) };

                    case "SendingFacility":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.SendingFacility}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.SendingFacility}
                                    />
                                </div>
                            )
                        };

                    case "Context":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.Context}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.Context}
                                    />
                                </div>
                            )
                        };

                    case "LabMessageDate":
                        return {
                            renderCell: item => {
                                var md = "";
                                if (item.Model.Model.PrelevmentDateTime != null) {
                                    var tmpDate = moment(item.Model.Model.PrelevmentDateTime);
                                    if (tmpDate.isBefore("2100-01-01"))
                                        md = tmpDate.format("YYYY/MM/DD");
                                }

                                return (
                                    <div title={md} className={item.Model.IsAbnormal ? "red-row" : ""}>
                                        <SpanControl
                                            IsRed={item.Model.IsAbnormal}
                                            IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            className={tableCellClass}
                                            text={md}
                                        />
                                    </div>
                                );
                            }
                        };

                    case "PortalStatus":
                        return {
                            renderCell: item => {
                                if (!this.isPortalEnabled ||
                                (!this.props.PatientModel?.IsRegisteredToPortal() && !cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) ||
                                !item.Model.Model.HasEncapsulatedData)
                                    return <div />;

                                const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(
                                    item.Model
                                );

                                return (
                                    <PortalStatusButton
                                        className="push-left-5"
                                        icon="share"
                                        onClick={e => this.PortalStatusClicked(item, e)}
                                        {...buttonProps}
                                    />
                                );
                            }
                        };

                    case "MessageSummary":
                        return {
                            renderCell: item => (
                                <div title={item.Model.Model.RequestSubjects}>
                                    <SpanControl
                                        IsRed={item.Model.IsAbnormal}
                                        IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                        className={tableCellClass}
                                        text={item.Model.Model.RequestSubjects}
                                    />
                                </div>
                            )
                        };

                    case "Status":
                        return {
                            renderCell: item => {
                                let statusValue = item.Model.Model.ResolvedLabStatus;
                                switch (statusValue) {
                                    case "ClassifiedByCollege":
                                        statusValue = cacheStore.GetString("ClassifiedByCollegeLabel");
                                        break;
                                    case "SubmittedByCollege":
                                        statusValue = cacheStore.GetString("SubmittedByCollege");
                                        break;
                                    case "Corrected":
                                        statusValue = cacheStore.GetString("CorrectedLabel");
                                        break;
                                    case "OnHoldLabsInbox":
                                        statusValue = cacheStore.GetString("OnHoldLabsInbox");
                                        break;
                                    case "NewLabel":
                                        statusValue = cacheStore.GetString("NewLabel");
                                        break;
                                    case "Classified":
                                        statusValue = cacheStore.GetString("ClassifiedLabel");
                                        break;
                                }

                                return (
                                    <div title={statusValue}>
                                        <FeatureFlagControl
                                            flag={cacheStore.ClinicSettings.EnableLabResultProcessingStatus}
                                            activeFeature={<LabReportCombinedStatusComponent LabMessage={item.Model} />}
                                            fallbackFeature={<LabReportReadingStatusComponent LabMessage={item.Model} />}
                                        />
                                    </div>
                                );
                            }
                        };

                    case "Detail":
                        if (cacheStore.ClinicSettings.EnableResultsFixes) {
                            return {
                                renderCell: item => (
                                    <div title={item.Model.Model.Detail}>
                                        <SpanControl
                                            IsRed={item.Model.IsAbnormal}
                                            IsBold={item.Model.Model.IsUnreadForQueryingClinician}
                                            className={tableCellClass}
                                            text={item.Model.Model.Detail}
                                        />
                                    </div>
                                )
                            };
                        }

                        return { renderCell: () => (null) };
                    case "Action":
                        return {
                            renderCell: item => {
                                let notesArray = item.Model.Model.ParentLevel_MyleNoteDtos;
                                let actionsArray = item.Model.Model.ParentLevel_MyleActionDtos;

                                let globalnotes = notesArray ? notesArray.filter(x => String.prototype.isNullOrWhiteSpace(x.ObservationId)) : [];
                                let obxnotes = notesArray ? notesArray.filter(x => !String.prototype.isNullOrWhiteSpace(x.ObservationId)) : [];

                                return (
                                    <div className="flex-container" style={{ justifyContent: "flex-start", width: "80px" }}>
                                        {globalnotes.length > 0 &&
                                            <a style={{ marginLeft: "0px" }} onClick={e => this.sendActionNoteOperation(item.Model.Model, e)}>{"[ "}<span className="red-text">{globalnotes.length}</span>{" ]"}</a>
                                        }
                                        {globalnotes.length == 0 &&
                                            <a style={{ marginLeft: "0px" }} onClick={e => this.sendActionNoteOperation(item.Model.Model, e)}>[ + ]</a>
                                        }
                                        {(obxnotes.length > 0 || actionsArray?.length > 0) &&
                                            <a style={{ marginLeft: "6px" }} onClick={() => this.popupObxActionWarning()}><span style={{ color: "red", position: "absolute", marginTop: "-4px", fontSize: "20px" }}>{" ! "}</span></a>
                                        }
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private PortalStatusClicked(item: LabMessageTableItem, e: React.MouseEvent): void {
        $(e.target).closest("tr").addClass("active");

        sessionStore.dispatch(
            LocalOperations.LabsUI.PortalStatusClicked,
            {
                lab: item.Model,
                patientContactId: this.props.PatientContactId,
                registrationStatus: this.props.PatientModel?.IndividualResponse?.PortalSyncState
            }
        );
    }

    private sendActionNoteOperation(lab: LabMessageDTO, e: React.MouseEvent): void {
        const request = {
            labFillerId: lab.LabFillerID,
            observation: null,
            notes: this.selectResultNotes(lab.ParentLevel_MyleNoteDtos),
            actions: lab.ParentLevel_MyleActionDtos ?? [],
            event: e
        };

        if (cacheStore.ClinicSettings.EnableResultsFixes) {
            sessionStore.dispatch(
                LocalOperations.LabsUI.ResultDetailClick,
                {
                    ...request,
                    detail: lab.Detail,
                    isManualLabEntry: lab.IsManualLabEntry ?? false
                }
            );

            return;
        }

        sessionStore.dispatch(
            LocalOperations.LabsUI.NotesClick,
            request
        );
    }

    private popupObxActionWarning(): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("InformationLabel"),
            content: cacheStore.GetString("LabObservationWarningLabel")
        });
    }

    private onDSQLabsClick(): void {
        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplayDSQLabsGridPopup,
            {
                patientContactId: this.props.PatientContactId
            }
        );
    }

    private onDSQImgsClick(): void {
        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplayDSQImgsGridPopup,
            {
                patientContactId: this.props.PatientContactId
            }
        );
    }

    private selectResultNotes(observationAndResultNotes: MyleNoteDTO[]): MyleNoteDTO[] {
        return observationAndResultNotes
            ? observationAndResultNotes.filter(note => !note.ObservationId)
            : [];
    }
}

export { PatientLabsInboxView };
